import { useMemo, useRef, useEffect } from "react";
import { MapContainer, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "leaflet.markercluster";

// Institutional colors per waste type (fraction).
const WASTE_COLORS = {
  "Envases mezclados": "#F4A261",         // amarillo
  "Papel y cartón": "#0B4C7A",            // azul
  "Vidrio": "#10B981",                    // verde
  "Envases de vidrio": "#10B981",
  "Fracción resto": "#6B7280",            // gris
  "Orgánico": "#7C4A28",                  // marrón
  "Materia orgánica": "#7C4A28",
  "Textil": "#E76F51",
  "Aceite doméstico usado": "#F4E4C1",
  "Escombros": "#9CA3AF",
  "Electrodomésticos": "#2A9D8F",
  "Pilas": "#5B21B6",
  default: "#0B4C7A",
};

function colorFor(cp) {
  const key = cp?.waste_type || cp?.type_name || "default";
  return WASTE_COLORS[key] || WASTE_COLORS.default;
}

function ClusterLayer({ colPoints, onSelect, viewDetailLabel }) {
  const map = useMap();
  const groupRef = useRef(null);

  useEffect(() => {
    if (!groupRef.current) {
      groupRef.current = L.markerClusterGroup({
        showCoverageOnHover: false,
        maxClusterRadius: 50,
        iconCreateFunction: (cluster) => {
          const n = cluster.getChildCount();
          const size = n > 100 ? 46 : n > 30 ? 40 : 34;
          return L.divIcon({
            html: `<div style="background:#0B4C7A;color:white;border-radius:50%;width:${size}px;height:${size}px;display:flex;align-items:center;justify-content:center;font:600 12px Inter;border:2px solid #FBFAF7;box-shadow:0 2px 6px rgba(0,0,0,.25)">${n}</div>`,
            className: "sigeus-cluster",
            iconSize: [size, size],
          });
        },
      });
      map.addLayer(groupRef.current);
    }
    const g = groupRef.current;
    g.clearLayers();
    (colPoints || []).forEach((cp) => {
      if (cp.latitude == null || cp.longitude == null) return;
      const color = colorFor(cp);
      const m = L.circleMarker([cp.latitude, cp.longitude], {
        radius: 6,
        color: "#FFFFFF",
        weight: 1.5,
        fillColor: color,
        fillOpacity: 0.9,
      });
      const popup = `
        <div style="font:12px Inter,sans-serif;min-width:200px">
          <div style="font-family:'Playfair Display',serif;font-size:14px;color:#0B4C7A;font-weight:600">
            ${cp.reference || cp.external_code || `#${cp.col_point_id}`}
          </div>
          <div style="color:#6B7280;font-size:11px;margin:4px 0">
            ${cp.address || cp.postal_code || "—"}
          </div>
          <div style="color:#6B7280;font-size:10px">
            ${cp.frequency_days ? `Frec.: ${cp.frequency_days} días` : ""}
          </div>
          <button
            id="cp-btn-${cp.col_point_id}"
            style="margin-top:8px;background:#10B981;color:#fff;border:0;border-radius:8px;padding:6px 12px;font-size:11px;cursor:pointer;letter-spacing:0.18em;text-transform:uppercase"
          >${viewDetailLabel}</button>
        </div>`;
      m.bindPopup(popup, { minWidth: 220 });
      m.on("popupopen", () => {
        setTimeout(() => {
          const btn = document.getElementById(`cp-btn-${cp.col_point_id}`);
          if (btn) btn.onclick = () => onSelect?.(cp);
        }, 30);
      });
      g.addLayer(m);
    });
    return () => {};
  }, [colPoints, onSelect, viewDetailLabel, map]);

  return null;
}

export default function ContenedoresMap({ colPoints = [], onSelect, viewDetailLabel = "Ver detalle" }) {
  const center = useMemo(() => {
    if (colPoints.length) {
      const lat = colPoints[0].latitude;
      const lon = colPoints[0].longitude;
      if (lat && lon) return [lat, lon];
    }
    return [42.55, -8.78];
  }, [colPoints]);

  return (
    <div className="h-full w-full rounded-2xl overflow-hidden border hairline" data-testid="contedores-map">
      <MapContainer center={center} zoom={11} scrollWheelZoom={true} style={{ height: "100%", width: "100%" }}>
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
          attribution='&copy; OpenStreetMap · CARTO · siGEUS'
        />
        <ClusterLayer colPoints={colPoints} onSelect={onSelect} viewDetailLabel={viewDetailLabel} />
      </MapContainer>
    </div>
  );
}
